import { useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TOAST_MSG, URL_PATH } from '../../constants';
import { constructFileImportReducer, FILE_IMPORT_ACTIONS, initialImportState } from '../../modules/services/service-wrapper/helper/ImportFileData';
import { addImportedExceptions, addInvalidExceptions, clearAllExceptions } from '../../store/reducer/services/ExceptionsReducer';
import { addServiceDetails, deleteServiceDetails, updateImportStatus } from '../../store/reducer/services/ServiceDetailsReducer';
import { addAllServiceOperations, clearServiceOperations, updateSOImportStatus } from '../../store/reducer/services/ServiceOperationReducer';
import { duplicateArrayStringRemoval, exceptionDataMapper, serviceDetailImportMapper, serviceOperationImportMapper } from '../../utilities/services/ServiceWrapperUtil';
import useDuplicateExceptionCheck from '../../store/_api/services/DuplicateExceptionCheck';

/**
 * @method useImportFileData
 * @description custom hook to manage import file
 * @param failure callback function to show failure toast message
 */
export const useImportFileData = (failure: (title: string, message: string) => void) => {
  const [importState, dispatchImportFile] = useReducer(constructFileImportReducer, initialImportState);
  const dispatch = useDispatch();
  const history = useNavigate();
  const { checkDuplicateException } = useDuplicateExceptionCheck();

  const updateImportFileData = (fileData: { [key: string]: any }) => {
    history(`${URL_PATH.SERVICE_HOME}${URL_PATH.CREATE_SERVICE}`);
    dispatchImportFile({
      type: FILE_IMPORT_ACTIONS.IMPORT_STARTED,
      payload: { importSwaggerLoading: true, uploadFileName: fileData.fileName, fileNotificationStatus: true, fileUploadStatus: fileData.status }
    });
    dispatch(deleteServiceDetails());
    dispatch(clearServiceOperations());
    dispatch(clearAllExceptions());
    if (fileData.status) {
      dispatch(updateImportStatus(true));
      const serviceDetailsToStore = serviceDetailImportMapper(fileData.data);
      dispatch(addServiceDetails(serviceDetailsToStore));
      const { serviceOperationsData, serviceExceptionsData } = serviceOperationImportMapper(fileData.data);
      if (serviceExceptionsData.length > 0) {
        const validExceptions = checkDuplicateException(serviceExceptionsData.map((val) => val.exceptionName.replace(/\s/g, '')));
        validExceptions.then((data) => {
          if (data?.validExceptionList && data?.validExceptionList?.length > 0) {
            const { mappedExceptionData, invalidExceptions } = exceptionDataMapper(serviceExceptionsData, data.validExceptionList);
            dispatch(addImportedExceptions(mappedExceptionData));
            dispatch(addInvalidExceptions(invalidExceptions));
          } else if (data.isError) {
            failure('Error', `Exception Validation failed. ${TOAST_MSG.pls_try_again}`);
          } else {
            dispatch(addInvalidExceptions(duplicateArrayStringRemoval(serviceExceptionsData.map((val) => val.exceptionName))));
          }
          dispatchImportFile({ type: FILE_IMPORT_ACTIONS.IMPORT_LOADING, payload: { importSwaggerLoading: false } });
        });
      } else dispatchImportFile({ type: FILE_IMPORT_ACTIONS.IMPORT_LOADING, payload: { importSwaggerLoading: false } });
      dispatch(addAllServiceOperations(serviceOperationsData));
      dispatch(updateSOImportStatus(true));
    } else dispatchImportFile({ type: FILE_IMPORT_ACTIONS.IMPORT_LOADING, payload: { importSwaggerLoading: false } });
  };

  const closeImportNotification = () => {
    dispatchImportFile({ type: FILE_IMPORT_ACTIONS.CLOSE_NOTIFICATION });
  };

  return { importState, updateImportFileData, closeImportNotification };
};
